import { useState, useEffect } from 'react'

interface CompanyStats {
  totalContent: number
  presentationCount: number
  pdfCount: number
  imageCount: number
  videoCount: number
  userCount: number
}

interface UseCompanyStatsResult {
  stats: CompanyStats | null
  loading: boolean
  error: string | null
  refetch: () => void
}

export function useCompanyStats(): UseCompanyStatsResult {
  const [stats, setStats] = useState<CompanyStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStats = async () => {
    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('No authentication token found')
      }

      // Fetch content data
      const contentResponse = await fetch('http://localhost:3000/api/content', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!contentResponse.ok) {
        throw new Error('Failed to fetch content statistics')
      }

      const contentData = await contentResponse.json()
      const contentItems = contentData.content || []

      // Calculate content stats
      const presentationCount = contentItems.filter((item: any) => item.type === 'presentation').length
      const pdfCount = contentItems.filter((item: any) => item.type === 'pdf').length
      const imageCount = contentItems.filter((item: any) => item.type === 'image').length
      const videoCount = contentItems.filter((item: any) => item.type === 'video').length

      // Fetch user count
      let userCount = 0
      try {
        const usersResponse = await fetch('http://localhost:3000/api/company/users', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (usersResponse.ok) {
          const usersData = await usersResponse.json()
          userCount = usersData.users?.length || 0
        }
      } catch (err) {
        console.warn('Failed to fetch user count:', err)
        // Continue with userCount = 0
      }

      setStats({
        totalContent: contentItems.length,
        presentationCount,
        pdfCount,
        imageCount,
        videoCount,
        userCount
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred')
      console.error('Error fetching company stats:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  return { stats, loading, error, refetch: fetchStats }
}

export type { CompanyStats, UseCompanyStatsResult }
